import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { Clock, LogIn, LogOut, Calendar, FileText, TrendingUp, CheckCircle, AlertCircle } from "lucide-react";
import { useTheme } from "context/ThemeContext";

import { apiBaseUrl } from '../../../services/config';
const API = apiBaseUrl;

const fmt = (n) => `₹${Math.round(n||0).toLocaleString("en-IN")}`;

export default function EmployeeDashboard() {
  const { T }     = useTheme();
  const navigate  = useNavigate();
  const userData  = JSON.parse(sessionStorage.getItem("platform_user") || "{}");
  const platformId = userData.id;
  const token      = sessionStorage.getItem("platform_token") || "";
  const ax         = { headers:{ Authorization:`Bearer ${token}` } };

  const [data,    setData]    = useState(null);
  const [loading, setLoading] = useState(true);
  const [checkingIn,  setCheckingIn]  = useState(false);
  const [checkingOut, setCheckingOut] = useState(false);
  const [message, setMessage] = useState("");

  const load = async () => {
    try {
      const r = await axios.get(`${API}/employee-portal/dashboard?platform_user_id=${platformId}`, ax);
      setData(r.data);
    } catch(e) { console.error(e); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (platformId) load(); }, [platformId]);

  const handleCheckIn = async () => {
    setCheckingIn(true); setMessage("");
    try {
      const r = await axios.post(`${API}/employee-portal/checkin?platform_user_id=${platformId}`, {}, ax);
      setMessage(`✅ ${r.data.message}`); load();
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail || "Error"}`); }
    finally { setCheckingIn(false); }
  };

  const handleCheckOut = async () => {
    setCheckingOut(true); setMessage("");
    try {
      const r = await axios.post(`${API}/employee-portal/checkout?platform_user_id=${platformId}`, {}, ax);
      setMessage(`✅ ${r.data.message}`); load();
    } catch(e) { setMessage(`❌ ${e.response?.data?.detail || "Error"}`); }
    finally { setCheckingOut(false); }
  };

  if (loading) return <div style={{ display:"flex", alignItems:"center", justifyContent:"center", padding:80, color:T.muted, fontFamily:"Outfit,sans-serif", gap:12, fontSize:14 }}>
    <div style={{ width:28, height:28, border:`2px solid ${T.border}`, borderTopColor:T.cyan, borderRadius:"50%", animation:"spin 0.8s linear infinite" }}/>Loading your dashboard…
    <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
  </div>;

  const name = userData.full_name || userData.email?.split("@")[0] || "there";
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";

  const card = { background:T.card, border:`1px solid ${T.border}`, borderRadius:12, fontFamily:"'Outfit',sans-serif" };

  return (
    <div style={{ fontFamily:"'Outfit',sans-serif", color:T.text }}>
      {/* Greeting */}
      <div style={{ marginBottom:24 }}>
        <div style={{ fontSize:22, fontWeight:800, marginBottom:4 }}>{greeting}, {name.split(" ")[0]} 👋</div>
        <div style={{ fontSize:13, color:T.muted }}>{new Date().toLocaleDateString("en-IN",{weekday:"long",day:"numeric",month:"long",year:"numeric"})}</div>
      </div>

      {/* Check-in/out widget */}
      <div style={{ ...card, marginBottom:20, padding:0, overflow:"hidden", border:`1px solid ${T.cyan}26` }}>
        <div style={{ background:`linear-gradient(135deg,${T.cyan}14,${T.purple}14)`, padding:"18px 24px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center", flexWrap:"wrap", gap:14 }}>
          <div>
            <div style={{ fontSize:12, color:T.muted, textTransform:"uppercase", letterSpacing:"0.08em", marginBottom:4 }}>TODAY'S ATTENDANCE</div>
            <div style={{ display:"flex", gap:20, flexWrap:"wrap" }}>
              <div style={{ fontSize:13, color:T.muted }}>
                Check-in: <strong style={{ color:data?.check_in_time?T.green:T.dim }}>{data?.check_in_time || "—"}</strong>
              </div>
              <div style={{ fontSize:13, color:T.muted }}>
                Check-out: <strong style={{ color:data?.check_out_time?T.cyan:T.dim }}>{data?.check_out_time || "—"}</strong>
              </div>
              {data?.work_hours > 0 && (
                <div style={{ fontSize:13, color:T.muted }}>
                  Hours: <strong style={{ color:data.work_hours>=8?T.green:data.work_hours>=4?T.amber:T.red }}>{data.work_hours}h</strong>
                </div>
              )}
            </div>
          </div>
          <div style={{ display:"flex", gap:10 }}>
            {!data?.checked_in ? (
              <button onClick={handleCheckIn} disabled={checkingIn}
                style={{ display:"inline-flex", alignItems:"center", gap:8, background:`linear-gradient(135deg,${T.green},${T.cyan})`, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:checkingIn?"not-allowed":"pointer", fontFamily:"Outfit,sans-serif", opacity:checkingIn?0.7:1 }}>
                <LogIn size={15}/> {checkingIn?"Checking in…":"Check In"}
              </button>
            ) : !data?.check_out_time ? (
              <button onClick={handleCheckOut} disabled={checkingOut}
                style={{ display:"inline-flex", alignItems:"center", gap:8, background:`linear-gradient(135deg,${T.purple},${T.red})`, border:"none", borderRadius:8, color:"#fff", fontSize:13, fontWeight:700, padding:"10px 20px", cursor:checkingOut?"not-allowed":"pointer", fontFamily:"Outfit,sans-serif", opacity:checkingOut?0.7:1 }}>
                <LogOut size={15}/> {checkingOut?"Checking out…":"Check Out"}
              </button>
            ) : (
              <div style={{ fontSize:13, padding:"10px 16px", borderRadius:8, background:`${T.green}1A`, color:T.green, border:`1px solid ${T.green}33`, display:"flex", alignItems:"center", gap:6 }}>
                <CheckCircle size={14}/> Attendance marked
              </div>
            )}
          </div>
        </div>
        {message && (
          <div style={{ padding:"10px 24px", fontSize:13, color: message.startsWith("✅")?T.green:T.red, borderTop:`1px solid ${T.cyan}0F` }}>
            {message}
          </div>
        )}
      </div>

      {/* Quick stats */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14, marginBottom:20 }}>
        {[
          { label:"This Month",  val:`${(data?.month_attendance?.present||0)} days`, sub:"Present", color:T.green, icon:CheckCircle, route:"/employee/attendance" },
          { label:"On Leave",    val:data?.month_attendance?.absent||0,              sub:"Days absent",color:T.red,icon:Calendar, route:"/employee/leaves" },
          { label:"Leaves Pending",val:data?.pending_leaves||0,                     sub:"Awaiting approval",color:T.amber,icon:Clock, route:"/employee/leaves" },
          { label:"Latest Pay",  val:data?.latest_payslip ? fmt(data.latest_payslip.net_salary):"—", sub:data?.latest_payslip?.month||"No payslip yet",color:T.cyan,icon:FileText, route:"/employee/payslips" },
        ].map(({ label, val, sub, color, icon:Icon, route }) => (
          <div key={label} style={{ ...card, padding:18, cursor:"pointer", borderTop:`3px solid ${color}`, transition:"transform 0.2s" }}
            onClick={() => navigate(route)}
            onMouseEnter={e=>e.currentTarget.style.transform="translateY(-3px)"}
            onMouseLeave={e=>e.currentTarget.style.transform=""}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:12 }}>
              <div style={{ width:32, height:32, borderRadius:8, background:`${color}20`, display:"flex", alignItems:"center", justifyContent:"center", color }}>
                <Icon size={15}/>
              </div>
            </div>
            <div style={{ fontSize:22, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color, marginBottom:3 }}>{val}</div>
            <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.07em" }}>{label}</div>
            <div style={{ fontSize:11.5, color:T.dim, marginTop:2 }}>{sub}</div>
          </div>
        ))}
      </div>

      {/* Leave balance + Month summary */}
      <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
        {/* Leave balance */}
        <div style={{ ...card }}>
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ margin:0, fontSize:14, fontWeight:600 }}>Leave Balance {new Date().getFullYear()}</h3>
            <button onClick={() => navigate("/employee/leaves")} style={{ fontSize:11.5, color:T.cyan, background:"none", border:"none", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>
              Apply Leave →
            </button>
          </div>
          <div style={{ padding:"14px 18px" }}>
            {(data?.leave_balances || []).length === 0 ? (
              <div style={{ textAlign:"center", padding:20, color:T.muted, fontSize:13 }}>No leave balance configured</div>
            ) : (data.leave_balances || []).map(lb => (
              <div key={lb.name} style={{ marginBottom:14 }}>
                <div style={{ display:"flex", justifyContent:"space-between", marginBottom:5 }}>
                  <div style={{ display:"flex", alignItems:"center", gap:7 }}>
                    <div style={{ width:9, height:9, borderRadius:2, background:lb.color }}/>
                    <span style={{ fontSize:13, color:T.text }}>{lb.name}</span>
                  </div>
                  <span style={{ fontSize:12, fontFamily:"JetBrains Mono,monospace", color:lb.color, fontWeight:600 }}>{lb.balance} / {lb.allocated}</span>
                </div>
                <div style={{ height:5, background:T.toggle_bg, borderRadius:5, overflow:"hidden" }}>
                  <div style={{ width:`${lb.allocated>0?(lb.balance/lb.allocated*100):0}%`, height:"100%", background:lb.color, borderRadius:5, transition:"width 0.4s" }}/>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* This month attendance summary */}
        <div style={{ ...card }}>
          <div style={{ padding:"14px 18px", borderBottom:`1px solid ${T.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <h3 style={{ margin:0, fontSize:14, fontWeight:600 }}>This Month Summary</h3>
            <button onClick={() => navigate("/employee/attendance")} style={{ fontSize:11.5, color:T.cyan, background:"none", border:"none", cursor:"pointer", fontFamily:"Outfit,sans-serif" }}>View All →</button>
          </div>
          <div style={{ padding:"18px", display:"grid", gridTemplateColumns:"1fr 1fr", gap:12 }}>
            {[
              { label:"Present",  val:data?.month_attendance?.present ||0, color:T.green },
              { label:"WFH",      val:data?.month_attendance?.wfh     ||0, color:T.cyan },
              { label:"Half Day", val:data?.month_attendance?.half_day||0, color:T.amber },
              { label:"Absent",   val:data?.month_attendance?.absent  ||0, color:T.red },
              { label:"Late",     val:data?.month_attendance?.late    ||0, color:T.purple },
            ].map(({ label, val, color }) => (
              <div key={label} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 12px", background:T.inp_bg, borderRadius:8, border:`1px solid ${color}33` }}>
                <div style={{ fontSize:20, fontWeight:800, fontFamily:"JetBrains Mono,monospace", color }}>{val}</div>
                <div style={{ fontSize:11, color:T.muted, textTransform:"uppercase", letterSpacing:"0.06em" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
